import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AdminTenantCreate, AdminTenantUserCreate } from "@gb/admin-contracts";
import { api, errorMessage } from "../../api/client";

export const tenantKeys = {
  all: ["tenants"] as const,
  list: () => [...tenantKeys.all, "list"] as const,
  detail: (tenantId: string) => [...tenantKeys.all, tenantId] as const,
  stats: (tenantId: string) => [...tenantKeys.all, tenantId, "stats"] as const,
  users: (tenantId: string) => [...tenantKeys.all, tenantId, "users"] as const,
};

export function useTenants() {
  return useQuery({
    queryKey: tenantKeys.list(),
    queryFn: async () => {
      const res = await api.tenants.list();
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.tenants;
    },
  });
}

export function useTenant(tenantId: string) {
  return useQuery({
    queryKey: tenantKeys.detail(tenantId),
    queryFn: async () => {
      const res = await api.tenants.get({ params: { tenantId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.tenant;
    },
  });
}

/**
 * Fetched lazily per visible row (staleTime 60s): the tenant list stays a
 * registry read that one broken tenant schema cannot slow or sink.
 */
export function useTenantStats(tenantId: string) {
  return useQuery({
    queryKey: tenantKeys.stats(tenantId),
    staleTime: 60_000,
    queryFn: async () => {
      const res = await api.tenants.stats({ params: { tenantId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdminTenantCreate) => {
      const res = await api.tenants.create({ body: input });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.tenant;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: tenantKeys.list() }),
  });
}

/**
 * The 201 body carries the generated one-time password. It lives only in the
 * caller's state until dismissed — nothing here caches it.
 */
export function useCreateUser(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdminTenantUserCreate) => {
      const res = await api.tenants.createUser({ params: { tenantId }, body: input });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: tenantKeys.users(tenantId) }),
  });
}

/**
 * The dashboard's debug view, on or off for one account (`architecture/0029`).
 * The one place the flag is set without a database console. The tenant's
 * debug routes read it on every request, so it takes effect at once.
 */
export function useSetUserDebug(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; canDebug: boolean }) => {
      const res = await api.tenants.setUserDebug({
        params: { tenantId, userId: input.userId },
        body: { canDebug: input.canDebug },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.user;
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: tenantKeys.users(tenantId) }),
  });
}

export function useTenantUsers(tenantId: string) {
  return useQuery({
    queryKey: tenantKeys.users(tenantId),
    queryFn: async () => {
      const res = await api.tenants.users({ params: { tenantId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.users;
    },
  });
}

/**
 * Record the tenant's Microsoft 365 organisation for SharePoint sync, or clear
 * it (`plans/sharepoint-sync.md`, question 26). The answer is the tenant as
 * just written, which replaces the cached one: a re-read could still be the
 * API's 30-second-old copy.
 */
export function useSetMicrosoft(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (organisation: string | null) => {
      const res = await api.tenants.setMicrosoft({ params: { tenantId }, body: { tenantId: organisation } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.tenant;
    },
    onSuccess: (tenant) => queryClient.setQueryData(tenantKeys.detail(tenantId), tenant),
  });
}

/** The admin-consent address for the tenant's Microsoft 365 admin, asked for when wanted. */
export function useMicrosoftConsentLink(tenantId: string) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.tenants.microsoftConsentLink({ params: { tenantId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.url;
    },
  });
}
